import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import type { CreateConsumerRequest } from '../types/consumer.types';
import type { ConsumerSearchResult } from '../types/search.types';
import { getCustomerCardBffBaseUrl } from '../config';
import * as customerCardSelectors from './selectors';
import { CREATE_CONSUMER_REQUEST, CREATE_CONSUMER_SUCCESS, CREATE_CONSUMER_FAILURE } from './consumerActions';
import { SEARCH_REQUEST, SEARCH_SUCCESS, SEARCH_FAILURE } from './cardOperationActions';

type ConsumerThunkDispatch = (action: any) => any;
type ConsumerThunkGetState = () => CustomerCardStoreState & Parameters<typeof dealerSelectors.getDealerId>[0];

/** Business individuals are excluded for now — the consumer row has no treatment for them yet. */
const INCLUDE_BUSINESS_INDIVIDUALS = false;

const consumerHeaders = (dealId: string, dealerId: string, commonOrgId: string): Record<string, string> => ({
    'Content-Type': 'application/json',
    'x-dr-dealxgid': dealId,
    'x-dr-dealerid': dealerId,
    'x-dr-dealer-common-org-id': commonOrgId
});

/**
 * Full-text search over Common Consumer, scoped by the `x-dr-dealer-common-org-id` header. The lambda
 * never reads the deal id, so requiring one would block searching on a quote with no DealXG record yet.
 */
export const searchConsumersByName =
    (query: string) => async (dispatch: ConsumerThunkDispatch, getState: ConsumerThunkGetState) => {
        const state = getState();
        const bffBaseUrl = getCustomerCardBffBaseUrl();
        const dealId = customerCardSelectors.getDealId(state) || '';
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = customerCardSelectors.getCommonOrgId(state) || '';

        if (!bffBaseUrl || !commonOrgId) {
            dispatch({ type: SEARCH_FAILURE, payload: 'Missing configuration' });
            return;
        }

        dispatch({ type: SEARCH_REQUEST, payload: query });

        try {
            const response = await fetch(`${bffBaseUrl}/consumer/search`, {
                method: 'POST',
                headers: consumerHeaders(dealId, dealerId, commonOrgId),
                body: JSON.stringify({ query, includeBusinessIndividuals: INCLUDE_BUSINESS_INDIVIDUALS })
            });

            const responseBody = await response.json().catch(() => ({}));

            if (response.ok && responseBody?.ok) {
                dispatch({
                    type: SEARCH_SUCCESS,
                    payload: (responseBody.results as ConsumerSearchResult[]) || []
                });
            } else {
                dispatch({ type: SEARCH_FAILURE, payload: 'SearchFailed' });
            }
        } catch {
            dispatch({ type: SEARCH_FAILURE, payload: 'SearchFailed' });
        }
    };

/**
 * Creates a Common Consumer record for a participant the dealer entered by hand. The record belongs to
 * the dealership, not the deal; the lambda carries the deal id only on its logs and NR attributes.
 */
export const createConsumer =
    (requestBody: CreateConsumerRequest) => async (dispatch: ConsumerThunkDispatch, getState: ConsumerThunkGetState) => {
        const state = getState();
        const bffBaseUrl = getCustomerCardBffBaseUrl();
        const dealId = customerCardSelectors.getDealId(state) || '';
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = customerCardSelectors.getCommonOrgId(state) || '';

        if (!bffBaseUrl || !commonOrgId) {
            const error = 'Missing configuration';
            dispatch({ type: CREATE_CONSUMER_FAILURE, payload: error });
            return { ok: false as const, error };
        }

        dispatch({ type: CREATE_CONSUMER_REQUEST });

        try {
            const response = await fetch(`${bffBaseUrl}/consumer`, {
                method: 'POST',
                headers: consumerHeaders(dealId, dealerId, commonOrgId),
                body: JSON.stringify(requestBody)
            });

            const responseBody = await response.json().catch(() => ({}));
            const commonConsumerId = responseBody?.commonConsumerId;

            if (response.ok && responseBody?.ok && commonConsumerId) {
                dispatch({ type: CREATE_CONSUMER_SUCCESS, payload: responseBody });
                return { ok: true as const, commonConsumerId };
            }

            const error = responseBody?.error?.message || responseBody?.message || 'CreateConsumerFailed';
            dispatch({ type: CREATE_CONSUMER_FAILURE, payload: error });
            return { ok: false as const, error };
        } catch (error) {
            const errorMessage = (error as Error)?.message || 'CreateConsumerFailed';
            dispatch({ type: CREATE_CONSUMER_FAILURE, payload: errorMessage });
            return { ok: false as const, error: errorMessage };
        }
    };

export type ConsumerAction =
    | { type: typeof CREATE_CONSUMER_REQUEST }
    | { type: typeof CREATE_CONSUMER_SUCCESS; payload: Record<string, unknown> }
    | { type: typeof CREATE_CONSUMER_FAILURE; payload: string };
